/**
 * 烘干厂外设服务发布版本 API [T-151]
 *
 * 后端 Controller: hl-drying/.../PeripheralReleaseController#/drying/peripheral/**
 *   基础路径: /hl-drying/drying/peripheral
 *
 * 流程: 上传 zip (草稿) → 在列表点"发布" (草稿→已发布) → 现场客户端轮询 manifest 自动拉取更新.
 * 客户端接口 (manifest/download) 在后端 ShiroConfig 配置为免 token, 由现场 tray_app.py 直接调.
 */
import request from '@/utils/request'

const BASE = '/hl-drying/drying/peripheral'

/**
 * 上传 zip 安装包 (草稿状态).
 * @param {FormData} form  含 file / version / releaseNotes? / minClientVersion?
 * @param {(e:ProgressEvent)=>void} [onProgress]  上传进度回调
 */
export function uploadRelease(form, onProgress) {
  return request.post(BASE + '/release/upload', form, {
    headers: { 'Content-Type': 'multipart/form-data' },
    timeout: 300000,
    onUploadProgress: onProgress
  })
}

/** 发布版本 (DRAFT → PUBLISHED). 客户端可见. */
export function publishRelease(id) {
  return request.post(BASE + '/release/' + id + '/publish')
}

/** 归档版本 (PUBLISHED → ARCHIVED). 客户端不再可见, 历史保留. */
export function archiveRelease(id) {
  return request.post(BASE + '/release/' + id + '/archive')
}

/**
 * 版本列表 (分页, 管理端用).
 * @param {{ pageNo?: number, pageSize?: number, status?: string, version?: string }} params
 */
export function listReleases(params) {
  return request.get(BASE + '/release/list', { params: {
    pageNo: params.pageNo || 1,
    pageSize: params.pageSize || 20,
    status: params.status,
    version: params.version
  }})
}

/** 版本详情. */
export function getRelease(id) {
  return request.get(BASE + '/release/' + id)
}

/**
 * 查最新已发布安装包 manifest (后端免 token 客户端接口).
 * 不带 channel → 服务端回落 webbox 通道 (好粮宝桌面版安装包).
 * [2026-09-13] channel 可选: webbox / jinglun(组件增量) / jinglun-full(组件全量).
 * 返回 result: { latestVersion, packageUrl, size, sha256, releaseNotes, publishedAt, forced }
 *   latestVersion 为 null 表示当前无已发布版本;
 *   packageUrl 为 OSS 签名绝对 URL, 或本地模式的相对路径 (浏览器侧需拼 /api 前缀).
 */
export function fetchWebboxManifest(channel) {
  return request.get(BASE + '/manifest', { params: channel ? { channel } : {} })
}

// ============================================================
// [2026-09-17] 其他文件资产 — 管理员上传杂项文件 (地磅测试工具等),
// 上传后拿 downloadPath 拼完整地址给下载中心/现场用.
// 下载接口免 token: /api/hl-drying/drying/peripheral/asset/download/{id}
// ============================================================

/**
 * 上传其他文件 (上传即生效, 返回记录含 downloadPath).
 * @param {FormData} form  含 file / remark?
 * @param {(e:ProgressEvent)=>void} [onProgress]  上传进度回调
 */
export function uploadAsset(form, onProgress) {
  return request.post(BASE + '/asset/upload', form, {
    headers: { 'Content-Type': 'multipart/form-data' },
    timeout: 300000,
    onUploadProgress: onProgress
  })
}

/** 其他文件列表 (按上传时间倒序). */
export function listAssets() {
  return request.get(BASE + '/asset/list')
}

/** 删除其他文件记录 (不删文件实体). */
export function deleteAsset(id) {
  return request.post(BASE + '/asset/delete', { id })
}

/**
 * 拼「其他文件」完整下载地址 (浏览器可直接打开/复制给现场).
 * @param {string} downloadPath  后端返回的相对路径 (/hl-drying/drying/peripheral/asset/download/{id})
 */
export function assetFullUrl(downloadPath) {
  return window.location.origin + '/api' + downloadPath
}
